'use client';

import InlineLoadingBar from '../InlineLoadingBar/InlineLoadingBar';
import useModal from 'src/components/Modals/hooks/useModal';
import ResponsiveModal from 'src/components/Modals/ResponsiveModal/ResponsiveModal';
import Divider from '../primitives/Divider/Divider';
import { useState } from 'react';
import { format } from 'timeago.js';
import dayjs from 'dayjs';
import useInterval from 'src/hooks/useInterval';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import Link from 'src/components/primitives/Link/Link';
import { STANDARD_DATE_FORMAT } from 'src/constants/dates';
import { toNotional } from 'src/utils/formatting';
import { DetailsRow } from './InsuranceFundDetailsModal';
import { useInsuranceFundStatsQuery } from 'src/hooks/query/useInsuranceFundStatsQuery';

const TimeAgoWithTooltip = ({ date }: { date: Date }) => {
	const [timeAgoString, setTimeAgoString] = useState(format(date));

	// update timeAgo string every 10 seconds
	useInterval(() => {
		setTimeAgoString(format(date));
	}, 10000);

	return (
		<Tooltip
			decoration="none"
			content={dayjs(date).format(`${STANDARD_DATE_FORMAT} h:mm:ss A`)}
		>
			{timeAgoString}
		</Tooltip>
	);
};

const RevenueContent = ({
	insuranceFundStats,
}: {
	insuranceFundStats: {
		totalRevenue: number;
		perpLiquidationTotal: number;
		spotLiquidationTotal: number;
	};
}) => {
	return (
		<div className="flex flex-col gap-y-xs">
			<span className="typo-body-xs text-text-secondary">Total Revenue</span>
			<ul className="flex list-disc flex-col gap-y-xs pl-4">
				<li>
					<div className="flex items-center justify-between">
						<span className="typo-body-xs text-text-secondary">
							Perp Revenue
						</span>
						<span className="text-right typo-body-xs text-text-default">
							{toNotional(insuranceFundStats.perpLiquidationTotal)}
						</span>
					</div>
				</li>
				<li>
					<div className="flex items-center justify-between">
						<span className="typo-body-xs text-text-secondary">
							Spot, Borrow/Lend Revenue
						</span>
						<span className="text-right typo-body-xs text-text-default">
							{toNotional(insuranceFundStats.spotLiquidationTotal)}
						</span>
					</div>
				</li>
			</ul>
		</div>
	);
};

const ModalContent = () => {
	const { data: insuranceFundStats } = useInsuranceFundStatsQuery();

	return insuranceFundStats ? (
		<>
			<div className="mb-sm flex w-full flex-col gap-y-xs">
				<span className="typo-label-sm text-text-default">
					7 Day Performance
				</span>
				<span className="block typo-body-xs text-text-secondary">
					The breakdown below reflects the last 7 day performance of all
					Insurance Fund Vaults. The performance of individual vaults is
					viewable via each vault card.
				</span>
			</div>
			<RevenueContent insuranceFundStats={insuranceFundStats} />
			<Divider />
			<div className="flex w-full flex-col gap-y-sm">
				<span className="typo-label-sm text-text-default">Bankruptcies</span>
				<div className="block">
					<span className="flex items-center gap-x-xs typo-body-xs text-text-secondary">
						{`You can view the history of bankruptcies `}
						<Link variant="secondary" href="/stats/bankruptcies" openInNewTab>
							here
						</Link>
						.
					</span>
				</div>
			</div>
			<div className="flex w-full flex-col gap-y-xs">
				<DetailsRow fullWidth={true}>
					<span className="typo-body-xs text-text-secondary">
						Last Perp Bankruptcy
					</span>
					<span className="text-right typo-body-xs text-text-secondary">
						<TimeAgoWithTooltip
							date={new Date(insuranceFundStats.lastPerpBankruptcy * 1000)}
						/>
					</span>
				</DetailsRow>
				<DetailsRow fullWidth={true}>
					<span className="typo-body-xs text-text-secondary">
						Last Spot Bankruptcy
					</span>
					<span className="text-right typo-body-xs text-text-secondary">
						<TimeAgoWithTooltip
							date={new Date(insuranceFundStats.lastSpotBankruptcy * 1000)}
						/>
					</span>
				</DetailsRow>
			</div>
		</>
	) : (
		<>
			<div className="flex h-full w-full items-center justify-center p-4">
				<InlineLoadingBar />
			</div>
		</>
	);
};

const InsuranceFundRevenueModal = () => {
	const { hide } = useModal();

	return (
		<ResponsiveModal.Container
			onClose={hide}
			dataTestId="insurance-fund-revenue-modal"
		>
			<ResponsiveModal.Title closeButtonTestId="insurance-fund-revenue-modal-close">
				Insurance Fund Performance
			</ResponsiveModal.Title>
			<ResponsiveModal.Body>
				<ModalContent />
			</ResponsiveModal.Body>
		</ResponsiveModal.Container>
	);
};

export default InsuranceFundRevenueModal;
